import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Types
export interface RoleItem {
  id: string;
  company: string;
  companyLogo: string;
  companyLogoBg: string;
  title: string;
  tag: string;
  location: string;
  type: string;
  eligibility: string;
  deadline: string;
  isQuickApply: boolean;
  field: string;
  description: string;
}

const ROLES_DATA: RoleItem[] = [
  {
    id: "scg-eng",
    company: "SCG",
    companyLogo: "SCG",
    companyLogoBg: "#e8f5ec",
    title: "Graduate Engineer",
    tag: "At SACC",
    location: "Rayong",
    type: "Graduate",
    eligibility: "Final year, engineering",
    deadline: "Apply by 5 Nov",
    isQuickApply: true,
    field: "Engineering",
    description:
      "Join SCG's forward-looking operations engineering team. Work on innovative chemical and packaging manufacturing processes, sustainability initiatives, and plant efficiency systems across Eastern Economic Corridor facilities.",
  },
  {
    id: "bbl-ma",
    company: "Bangkok Bank",
    companyLogo: "BBL",
    companyLogoBg: "#e9edf7",
    title: "Management Associate Programme",
    tag: "At SACC",
    location: "Bangkok",
    type: "Graduate",
    eligibility: "Final year or recent graduate, any subject",
    deadline: "Apply by 29 Oct",
    isQuickApply: true,
    field: "Business and consulting",
    description:
      "An intensive 24-month rotational leadership development curriculum across corporate banking, international banking networks, credit risk analytics, and fintech innovation units.",
  },
  {
    id: "bih-analyst",
    company: "Bumrungrad",
    companyLogo: "BIH",
    companyLogoBg: "#fcedec",
    title: "Clinical Data & Healthcare Analyst",
    tag: "At SACC",
    location: "Bangkok",
    type: "Internship",
    eligibility: "Any year, medicine, biomedical science or statistics",
    deadline: "Apply by 18 Nov",
    isQuickApply: true,
    field: "Healthcare",
    description:
      "Partner with world-class clinical informatics leaders to analyze patient outcomes, health informatics pipelines, and clinical operational efficiency at Southeast Asia's flagship international medical center.",
  },
  {
    id: "ais-swe",
    company: "AIS",
    companyLogo: "AIS",
    companyLogoBg: "#edf7e8",
    title: "Software Engineer & Cloud Solutions",
    tag: "At SACC",
    location: "Bangkok",
    type: "Internship",
    eligibility: "Computer science, software engineering or related",
    deadline: "Apply by 12 Nov",
    isQuickApply: true,
    field: "Technology and design",
    description:
      "Design scalable 5G network platforms, cloud infrastructure on AWS/GCP, and customer-facing digital applications serving over 45 million subscribers in Thailand.",
  },
  {
    id: "uob-ib",
    company: "UOB",
    companyLogo: "UOB",
    companyLogoBg: "#ebf2fa",
    title: "Commercial & Investment Banking Analyst",
    tag: "At SACC",
    location: "Bangkok",
    type: "Graduate",
    eligibility: "Final year, finance, economics or STEM",
    deadline: "Apply by 8 Nov",
    isQuickApply: true,
    field: "Finance and economics",
    description:
      "Conduct in-depth corporate financial modeling, syndicated debt evaluations, structured cross-border transactions, and treasury analysis across ASEAN client portfolios.",
  },
  {
    id: "pttep-energy",
    company: "PTTEP",
    companyLogo: "PTTEP",
    companyLogoBg: "#fbf2e5",
    title: "Sustainability & Energy Transition Associate",
    tag: "At SACC",
    location: "Bangkok",
    type: "Graduate",
    eligibility: "Engineering, earth sciences or environmental study",
    deadline: "Apply by 15 Nov",
    isQuickApply: true,
    field: "Science and research",
    description:
      "Participate in groundbreaking Carbon Capture and Storage (CCS) projects, renewable energy integration strategies, and environmental life-cycle modeling across Gulf of Thailand operations.",
  },
];

const EMPLOYERS = [
  { name: "Bangkok Bank", category: "Banking", code: "BBL", bg: "#e9edf7", color: "#163a7d" },
  { name: "AIS", category: "Technology", code: "AIS", bg: "#edf7e8", color: "#458118" },
  { name: "SCG", category: "Engineering", code: "SCG", bg: "#e8f5ec", color: "#1b6f3c" },
  { name: "UOB", category: "Banking", code: "UOB", bg: "#ebf2fa", color: "#003b7a" },
  { name: "Bumrungrad", category: "Healthcare", code: "BIH", bg: "#fcedec", color: "#9c2727" },
];

const FAQS = [
  {
    question: "Do I need an account to look?",
    answer:
      "No. Anyone can browse all open roles and employer information without creating an account. You only need a profile when you decide to submit an application.",
  },
  {
    question: "Does my application reach the company?",
    answer:
      "Yes, directly. Once submitted through CFAS, your verified profile and uploaded CV are transmitted straight to the recruitment and talent acquisition teams at that employer.",
  },
  {
    question: "Who sees my CV?",
    answer:
      "Only the verified recruitment teams at employers you explicitly submit an application to, alongside authorized SACC organizing officers for eligibility screening.",
  },
  {
    question: "Is my profile my SACC ticket?",
    answer:
      "No. Your CFAS profile is your careers fair application portal. You can easily link your SACC conference attendee ticket to your profile to view personalized booth numbers and fast-track physical queues.",
  },
  {
    question: "Is this only for business students?",
    answer:
      "No. Employers hire across engineering, technology, science, healthcare, finance, legal, arts, and research disciplines. Roles range from entry-level internships to graduate tracks.",
  },
];

export const CFASLanding: React.FC = () => {
  const navigate = useNavigate();
  const [selectedField, setSelectedField] = useState<string>("Any field");
  const [openFaq, setOpenFaq] = useState<number | null>(4); // Default 5th open as shown in screenshot
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filter roles based on selected study field
  const filteredRoles =
    selectedField === "Any field"
      ? ROLES_DATA
      : ROLES_DATA.filter(
          (role) =>
            role.field.toLowerCase() === selectedField.toLowerCase() ||
            role.eligibility.toLowerCase().includes(selectedField.toLowerCase())
        );

  const scrollToRoles = () => {
    navigate("/cfas/roles");
  };

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Montserrat", "Helvetica Neue", Arial, sans-serif',
        color: "#1c2e26",
        backgroundColor: "#f7f5ee",
        minHeight: "100vh",
        lineHeight: 1.5,
      }}
    >
      {/* ==================== 2. HERO SECTION ==================== */}
      <section
        style={{
          backgroundColor: "#03362a",
          padding: "68px 24px 84px",
          color: "#ffffff",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "48px",
            alignItems: "center",
          }}
        >
          {/* Left Column: Heading & CTA */}
          <div style={{ maxWidth: "620px" }}>
            <h1
              style={{
                fontSize: "clamp(36px, 5vw, 56px)",
                fontWeight: 800,
                lineHeight: 1.12,
                letterSpacing: "-0.5px",
                margin: "0 0 24px 0",
                color: "#ffffff",
              }}
            >
              Whatever you study, someone at SACC is hiring for it.
            </h1>
            <p
              style={{
                fontSize: "18.5px",
                color: "#bddcd1",
                margin: "0 0 36px 0",
                fontWeight: 400,
                maxWidth: "480px",
                lineHeight: 1.45,
              }}
            >
              Apply before the fair. Meet them on the day.
            </p>
            <button
              onClick={scrollToRoles}
              style={{
                backgroundColor: "#ffffff",
                color: "#03362a",
                border: "none",
                borderRadius: "10px",
                padding: "14px 30px",
                fontSize: "16px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                transition: "background-color 0.15s, transform 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f0f2f1";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Find roles
            </button>
          </div>

        </div>
      </section>

      {/* ==================== 3. STATS BAR ==================== */}
      <section
        style={{
          backgroundColor: "#f4f0e6",
          borderBottom: "1px solid #e5dfd2",
          padding: "36px 24px",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "36px",
          }}
        >
          {/* Stat 1 */}
          <div>
            <div
              style={{
                fontSize: "42px",
                fontWeight: 800,
                color: "#9c7237",
                lineHeight: 1.1,
                marginBottom: "4px",
              }}
            >
              819
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#54625b",
                fontSize: "14.5px",
                fontWeight: 500,
              }}
            >
              <span>applications through CFAS last year</span>
              <span
                style={{
                  backgroundColor: "#e7dfcd",
                  color: "#4e5953",
                  fontSize: "11.5px",
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: "10px",
                  letterSpacing: "0.2px",
                }}
              >
                verify
              </span>
            </div>
          </div>

          {/* Stat 2 */}
          <div>
            <div
              style={{
                fontSize: "42px",
                fontWeight: 800,
                color: "#9c7237",
                lineHeight: 1.1,
                marginBottom: "4px",
              }}
            >
              7
            </div>
            <div
              style={{
                color: "#54625b",
                fontSize: "14.5px",
                fontWeight: 500,
              }}
            >
              fields hiring, from engineering to healthcare
            </div>
          </div>

        </div>
      </section>

      {/* ==================== 4. ROLE PREVIEW REMOVED ==================== */}
      {false && (<section
        id="roles-section"
        style={{
          padding: "64px 24px",
          maxWidth: "1280px",
          margin: "0 auto",
        }}
      >
        {/* Header Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "28px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: 800,
                color: "#03362a",
                margin: 0,
                letterSpacing: "-0.2px",
              }}
            >
              A few of the roles open now
            </h2>
            {selectedField !== "Any field" && (
              <div
                style={{
                  color: "#5a6861",
                  fontSize: "14px",
                  marginTop: "4px",
                }}
              >
                Filtered by: <strong>{selectedField}</strong> (
                {filteredRoles.length} roles found) •{" "}
                <span
                  style={{
                    color: "#03362a",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => setSelectedField("Any field")}
                >
                  Show all
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate("/cfas/roles")}
            style={{
              background: "none",
              border: "none",
              color: "#03362a",
              fontWeight: 700,
              fontSize: "15px",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>See all 9 roles</span>
            <span>&rarr;</span>
          </button>
        </div>

        {/* Roles Cards List */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {filteredRoles.map((role) => (
            <div
              key={role.id}
              onClick={() => setSelectedRole(role)}
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "16px",
                border: "1px solid #e9e3d6",
                padding: "24px 28px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#c9bea8";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(3, 54, 42, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e9e3d6";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0,0,0,0.02)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "20px",
                }}
              >
                {/* Company Logo Badge */}
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "12px",
                    backgroundColor: role.companyLogoBg,
                    color: "#03362a",
                    fontWeight: 800,
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    letterSpacing: "0.2px",
                  }}
                >
                  {role.companyLogo}
                </div>

                {/* Role Details */}
                <div style={{ flex: 1 }}>
                  {/* Title & SACC tag */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                      marginBottom: "6px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#03362a",
                      }}
                    >
                      {role.title}
                    </h3>
                  </div>

                  {/* Company Name & Tag */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14.5px",
                        color: "#4a5951",
                        fontWeight: 600,
                      }}
                    >
                      {role.company}
                    </span>
                    <span
                      style={{
                        backgroundColor: "#f5eee0",
                        color: "#836224",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: "10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          backgroundColor: "#836224",
                        }}
                      />
                      {role.tag}
                    </span>
                  </div>

                  {/* Meta Details: Location, Type, Eligibility */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      flexWrap: "wrap",
                      color: "#5b6b63",
                      fontSize: "13.5px",
                      marginBottom: "16px",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>📍</span> {role.location}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>💼</span> {role.type}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>🎓</span> {role.eligibility}
                    </span>
                  </div>

                  {/* Footer Row: Deadline & Quick Apply */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                      paddingTop: "12px",
                      borderTop: "1px solid #f2ede2",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          color: "#91672c",
                          fontSize: "13.5px",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <span>📅</span> {role.deadline}
                      </span>
                      {role.isQuickApply && (
                        <span
                          style={{
                            backgroundColor: "#e0f2e9",
                            color: "#0f6f43",
                            fontSize: "12px",
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: "12px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <span
                            style={{
                              width: "5px",
                              height: "5px",
                              borderRadius: "50%",
                              backgroundColor: "#0f6f43",
                            }}
                          />
                          Quick apply
                        </span>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/cfas/apply/company");
                      }}
                      style={{
                        backgroundColor: "#03362a",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "7px 16px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Apply now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>)}

      {/* ==================== 5. HOW IT WORKS SECTION ==================== */}
      <section
        style={{
          backgroundColor: "#f4f0e6",
          padding: "72px 24px",
          borderTop: "1px solid #e7e0d3",
          borderBottom: "1px solid #e7e0d3",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: 800,
              color: "#03362a",
              marginBottom: "48px",
              letterSpacing: "-0.2px",
            }}
          >
            How it works
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "36px",
              position: "relative",
            }}
          >
            {/* Step 1 */}
            <div>
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  backgroundColor: "#d4b270",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#132b22",
                  marginBottom: "20px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                👤
              </div>
              <h3
                style={{
                  fontSize: "17.5px",
                  fontWeight: 700,
                  color: "#03362a",
                  margin: "0 0 10px 0",
                }}
              >
                1. Create your profile
              </h3>
              <p
                style={{
                  color: "#54625b",
                  fontSize: "14.5px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                Email, a 6 digit code, then one page: university, year, course,
                field. About two minutes.
              </p>
            </div>

            {/* Step 2 */}
            <div>
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  backgroundColor: "#d4b270",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#132b22",
                  marginBottom: "20px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                📄
              </div>
              <h3
                style={{
                  fontSize: "17.5px",
                  fontWeight: 700,
                  color: "#03362a",
                  margin: "0 0 10px 0",
                }}
              >
                2. Upload your CV once
              </h3>
              <p
                style={{
                  color: "#54625b",
                  fontSize: "14.5px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                PDF or Word. It goes with every application, so you never attach
                it twice.
              </p>
            </div>

            {/* Step 3 */}
            <div>
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  backgroundColor: "#d4b270",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#132b22",
                  marginBottom: "20px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                🔍
              </div>
              <h3
                style={{
                  fontSize: "17.5px",
                  fontWeight: 700,
                  color: "#03362a",
                  margin: "0 0 10px 0",
                }}
              >
                3. Apply before the fair
              </h3>
              <p
                style={{
                  color: "#54625b",
                  fontSize: "14.5px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                Filter by field and type. Every role shows who can apply and the
                deadline. Apply is one screen.
              </p>
            </div>

            {/* Step 4 */}
            <div>
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  backgroundColor: "#d4b270",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#132b22",
                  marginBottom: "20px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                📍
              </div>
              <h3
                style={{
                  fontSize: "17.5px",
                  fontWeight: 700,
                  color: "#03362a",
                  margin: "0 0 10px 0",
                }}
              >
                4. Meet them at SACC
              </h3>
              <p
                style={{
                  color: "#54625b",
                  fontSize: "14.5px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                Link your ticket and see which of your employers will be there,
                with booth numbers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 6. EMPLOYERS AT SACC SECTION ==================== */}
      <section
        style={{
          padding: "64px 24px",
          maxWidth: "1280px",
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            fontSize: "28px",
            fontWeight: 800,
            color: "#03362a",
            marginBottom: "32px",
            letterSpacing: "-0.2px",
          }}
        >
          Employers at SACC 2027
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
            gap: "16px",
          }}
        >
          {EMPLOYERS.map((emp) => (
            <div
              key={emp.name}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e9e3d6",
                borderRadius: "16px",
                padding: "20px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#c9bea8";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e9e3d6";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  backgroundColor: emp.bg,
                  color: emp.color,
                  fontWeight: 800,
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {emp.code}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "14.5px",
                    color: "#03362a",
                    lineHeight: 1.2,
                  }}
                >
                  {emp.name}
                </div>
                <div
                  style={{
                    fontSize: "12.5px",
                    color: "#6b7a72",
                    marginTop: "3px",
                  }}
                >
                  {emp.category}
                </div>
              </div>
            </div>
          ))}

          {/* "and 12 more" card */}
          <div
            style={{
              border: "1.5px dashed #c8beac",
              borderRadius: "16px",
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#54625b",
              fontWeight: 700,
              fontSize: "14.5px",
              cursor: "pointer",
              transition: "all 0.15s",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
            }}
            onClick={scrollToRoles}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#03362a";
              e.currentTarget.style.color = "#03362a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#c8beac";
              e.currentTarget.style.color = "#54625b";
            }}
          >
            and 12 more
          </div>
        </div>
      </section>

      {/* ==================== 7. QUESTIONS / FAQ SECTION ==================== */}
      <section
        style={{
          backgroundColor: "#f4f0e6",
          padding: "72px 24px",
          borderTop: "1px solid #e7e0d3",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "28px",
              fontWeight: 800,
              color: "#03362a",
              marginBottom: "32px",
              letterSpacing: "-0.2px",
            }}
          >
            Questions
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={faq.question}
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "14px",
                    border: "1px solid #e9e3d6",
                    overflow: "hidden",
                    transition: "all 0.15s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                  }}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    style={{
                      width: "100%",
                      padding: "20px 24px",
                      background: "none",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#03362a",
                      }}
                    >
                      {faq.question}
                    </span>
                    <span
                      style={{
                        fontSize: "22px",
                        fontWeight: 400,
                        color: "#03362a",
                        lineHeight: 1,
                      }}
                    >
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  {isOpen && (
                    <div
                      style={{
                        padding: "0 24px 22px 24px",
                        color: "#54625b",
                        fontSize: "14.5px",
                        lineHeight: 1.55,
                      }}
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== 8. BOTTOM CTA BANNER ==================== */}
      <section
        style={{
          backgroundColor: "#03362a",
          padding: "56px 24px",
          color: "#ffffff",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(24px, 3.5vw, 34px)",
              fontWeight: 800,
              margin: 0,
              color: "#ffffff",
              letterSpacing: "-0.3px",
            }}
          >
            Meet the employers you applied to at SACC 2027.
          </h2>

          <button
            onClick={scrollToRoles}
            style={{
              backgroundColor: "#ffffff",
              color: "#03362a",
              border: "none",
              borderRadius: "10px",
              padding: "14px 28px",
              fontSize: "15px",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f2f1";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ffffff";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Find roles
          </button>
        </div>
      </section>

      {/* ==================== 9. FOOTER ==================== */}
      <footer
        style={{
          backgroundColor: "#02261d",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "32px 24px",
          color: "#83a99c",
          fontSize: "13.5px",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div>
            <strong style={{ color: "#ffffff", fontWeight: 700 }}>
              Samaggi Samagom
            </strong>
            , CFAS, the career and SACC platform
          </div>

          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <a
              href="mailto:careers@samaggisamagom.com"
              style={{ color: "#a5c9be", textDecoration: "none" }}
            >
              careers@samaggisamagom.com
            </a>
            <span style={{ color: "rgba(255, 255, 255, 0.2)" }}>•</span>
            <span style={{ color: "#a5c9be", cursor: "pointer" }}>Privacy Policy</span>
            <span style={{ color: "rgba(255, 255, 255, 0.2)" }}>•</span>
            <span style={{ color: "#a5c9be", cursor: "pointer" }}>Terms</span>
          </div>
        </div>
      </footer>

      {/* ==================== ROLE DETAILS MODAL ==================== */}
      {selectedRole && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setSelectedRole(null)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              maxWidth: "560px",
              width: "100%",
              padding: "32px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedRole(null)}
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "none",
                border: "none",
                fontSize: "24px",
                color: "#6b7a72",
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  backgroundColor: selectedRole.companyLogoBg,
                  color: "#03362a",
                  fontWeight: 800,
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selectedRole.companyLogo}
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 800,
                    color: "#03362a",
                  }}
                >
                  {selectedRole.title}
                </h3>
                <div
                  style={{
                    color: "#4a5951",
                    fontWeight: 600,
                    fontSize: "15px",
                    marginTop: "2px",
                  }}
                >
                  {selectedRole.company} •{" "}
                  <span style={{ color: "#91672c" }}>{selectedRole.tag}</span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "14px",
                marginBottom: "24px",
                padding: "14px",
                backgroundColor: "#f7f5ee",
                borderRadius: "12px",
                fontSize: "14px",
                color: "#4a5951",
              }}
            >
              <div>📍 <strong>{selectedRole.location}</strong></div>
              <div>💼 <strong>{selectedRole.type}</strong></div>
              <div>🎓 <strong>{selectedRole.eligibility}</strong></div>
              <div>📅 <strong>{selectedRole.deadline}</strong></div>
            </div>

            <h4
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: 700,
                color: "#03362a",
              }}
            >
              Role Description
            </h4>
            <p
              style={{
                margin: "0 0 28px 0",
                color: "#54625b",
                fontSize: "14.5px",
                lineHeight: 1.6,
              }}
            >
              {selectedRole.description}
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  setSelectedRole(null);
                  navigate("/cfas/apply/company");
                }}
                style={{
                  flex: 1,
                  backgroundColor: "#03362a",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "13px",
                  fontSize: "15px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Apply with CFAS Profile
              </button>
              <button
                onClick={() => setSelectedRole(null)}
                style={{
                  backgroundColor: "#f0ece1",
                  color: "#03362a",
                  border: "none",
                  borderRadius: "10px",
                  padding: "13px 20px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CFASLanding;
